'use client';

import { memo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { FileText, FileCode2, FileJson, File, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tip } from '@/components/ui/tooltip';
import { useT } from '@/components/i18n-provider';
import { useContentSubscription } from '@/lib/content/use-content-subscription';
import { useChatHost } from '@/components/chat/chat-host-context';

interface DocumentChipProps {
  contentId: string;
  filename: string;
  mimeType: string;
  command?: string;
  diff?: { old_str?: string; new_str?: string; insert_line?: number };
  className?: string;
}

function getFileIcon(mimeType: string, filename: string) {
  if (/json/.test(mimeType) || filename.endsWith('.json')) return FileJson;
  if (/javascript|typescript|python|java|rust|go|ruby|php|css|html|xml|yaml|toml|sh|bash/.test(mimeType) ||
      /\.(js|ts|tsx|jsx|py|java|rs|go|rb|php|css|html|xml|yaml|yml|toml|sh|bash|c|cpp|h|hpp|swift|kt)$/i.test(filename)) return FileCode2;
  if (/text\//.test(mimeType) || /\.(txt|md|markdown|log|csv)$/i.test(filename)) return FileText;
  return File;
}

function getVerbBadge(command: string | undefined, t: (key: string, params?: Record<string, string | number>) => string): { label: string; className: string } {
  switch (command) {
    case 'create':
      return { label: t('tools.documentCreated'), className: 'bg-emerald-500/10 text-emerald-500' };
    case 'str_replace':
    case 'insert':
    case 'undo_edit':
      return { label: t('tools.documentEdited'), className: 'bg-blue-500/10 text-blue-500' };
    case 'replace':
      return { label: t('tools.documentUpdated'), className: 'bg-blue-500/10 text-blue-500' };
    case 'view':
      return { label: t('tools.documentViewed'), className: 'bg-muted text-muted-foreground' };
    default:
      return { label: t('tools.documentFile'), className: 'bg-muted text-muted-foreground' };
  }
}

export const DocumentChip = memo(function DocumentChip({ contentId, filename, mimeType, command, diff, className }: DocumentChipProps) {
  const router = useRouter();
  const t = useT();
  const Icon = getFileIcon(mimeType, filename);
  // When the chip is rendered inside a host that knows how to open the
  // content WITHOUT a full route change (e.g. an `AgentNode` on the
  // workspace canvas spawns a sibling content widget), it provides
  // `onOpenContent` via `ChatHostProvider`. If the host returns truthy
  // we suppress navigation; otherwise we fall through to `router.push`.
  const { onOpenContent } = useChatHost();

  // Live overlay: when an edit broadcast arrives for this content_id (typically
  // because a sub-agent finished a text_editor str_replace in a delegate
  // sub-chat), upgrade the badge from the message-frozen value (e.g. 'create'
  // captured when this row was first persisted) to the latest command. Without
  // this, the parent chat keeps showing "Created" forever even after follow-up
  // edits have superseded the original create.
  const [liveCommand, setLiveCommand] = useState<string | undefined>(undefined);
  const [liveDiff, setLiveDiff] = useState<DocumentChipProps['diff']>(undefined);
  useContentSubscription(contentId, (event) => {
    if (event.command) setLiveCommand(event.command);
    if (event.diff) setLiveDiff(event.diff);
  });

  const effectiveCommand = liveCommand ?? command;
  const effectiveDiff = liveDiff ?? diff;
  const badge = getVerbBadge(effectiveCommand, t);

  const tipLabel = effectiveDiff ? formatDiffTooltip(effectiveDiff, t) : filename;

  const handleClick = () => {
    if (onOpenContent) {
      const handled = onOpenContent(contentId, { filename, mimeType });
      // Sync truthy result → host claimed the click; nothing else to do.
      if (handled === true) return;
      // Async result: optimistically suppress the route change and let
      // the host resolve in the background. If the host rejects, we'd
      // rather show nothing than yank the user out of context.
      if (handled && typeof (handled as Promise<unknown>).then === 'function') {
        return;
      }
    }
    router.push(`/content/${contentId}`);
  };

  return (
    <Tip label={tipLabel}>
      <button
        onClick={handleClick}
        onMouseEnter={() => {
          if (!onOpenContent) router.prefetch(`/content/${contentId}`);
        }}
        onFocus={() => {
          if (!onOpenContent) router.prefetch(`/content/${contentId}`);
        }}
        className={cn(
          'group inline-flex items-center gap-2 h-8 pl-2.5 pr-3 rounded-full',
          'border border-border/50 bg-card',
          'hover:bg-muted/80 hover:scale-[1.02]',
          'transition-all duration-150 cursor-pointer',
          className
        )}
      >
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium truncate max-w-[200px]">{filename}</span>
        <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0', badge.className)}>
          {badge.label}
        </span>
        <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
      </button>
    </Tip>
  );
});

function formatDiffTooltip(diff: { old_str?: string; new_str?: string; insert_line?: number }, t: (key: string, params?: Record<string, string | number>) => string): string {
  if (diff.insert_line !== undefined && diff.new_str) {
    return `${t('tools.insertedAtLine', { line: diff.insert_line })}: ${diff.new_str.split('\n')[0].slice(0, 60)}`;
  }
  if (diff.old_str && diff.new_str) {
    return `- ${diff.old_str.split('\n')[0].slice(0, 40)}\n+ ${diff.new_str.split('\n')[0].slice(0, 40)}`;
  }
  return '';
}
